"use client"

import { Button } from "@/components/UI/button"
import { ChevronLeft, ChevronRight } from "lucide-react"

interface PaginationProps {
  currentPage: number
  totalPages: number
  totalCount: number
  perPage: number
  onPageChange: (page: number) => void
  loading?: boolean
}

export function Pagination({ 
  currentPage, 
  totalPages, 
  totalCount, 
  perPage, 
  onPageChange, 
  loading = false 
}: PaginationProps) {
  if (totalPages <= 1) return null

  const startItem = ((currentPage - 1) * perPage) + 1
  const endItem = Math.min(currentPage * perPage, totalCount)

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: number[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Smart pagination
      if (currentPage <= 3) {
        // Near the beginning
        for (let i = 1; i <= maxVisible; i++) {
          pages.push(i)
        }
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        // In the middle
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i)
        }
      }
    }

    return pages
  }

  const pageNumbers = getPageNumbers()

  return (
    <div className="flex items-center justify-between pt-6 border-t border-border">
      <div className="text-sm text-muted-foreground">
        Hiển thị <span className="font-medium">{startItem}</span> - <span className="font-medium">{endItem}</span> 
        trong tổng số <span className="font-medium">{totalCount}</span> kết quả
      </div>
      
      <div className="flex items-center gap-2">
        {/* Previous button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1 || loading}
          className="flex items-center gap-1"
        >
          <ChevronLeft className="w-4 h-4" />
          Trước
        </Button>
        
        {/* First page + ellipsis */}
        {pageNumbers[0] > 1 && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={loading}
              className="w-10"
            >
              1
            </Button>
            {pageNumbers[0] > 2 && (
              <span className="px-2 text-muted-foreground">...</span>
            )}
          </>
        )}
        
        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((pageNum) => (
            <Button
              key={pageNum}
              variant={pageNum === currentPage ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              className="w-10"
            >
              {pageNum}
            </Button>
          ))}
        </div>
        
        {/* Last page + ellipsis */}
        {pageNumbers[pageNumbers.length - 1] < totalPages && (
          <>
            {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
              <span className="px-2 text-muted-foreground">...</span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(totalPages)}
              disabled={loading}
              className="w-10"
            >
              {totalPages}
            </Button>
          </>
        )}
        
        {/* Next button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages || loading}
          className="flex items-center gap-1"
        >
          Sau
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
